import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const ADMIN_API_URL = '/api/admin/'; // Use the new admin panel URL
const MASTERS_API_URL = '/api/masters/'; // Keep for document types

const PermitConfiguration = ({ onClose }) => {
    const [mappings, setMappings] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { hasPermission } = usePermissions(); // 2. Get hook

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [mappingsRes, docTypesRes] = await Promise.all([
                fetch(`${ADMIN_API_URL}permit-document-mappings/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_URL}document-types/`, { headers: getAuthHeaders() })
            ]);

            if (!mappingsRes.ok) throw new Error('Failed to load permit mappings.');
            if (!docTypesRes.ok) throw new Error('Failed to load document types.');

            const mappingsData = await mappingsRes.json();
            const docTypesData = await docTypesRes.json();

            setMappings(mappingsData);
            setDocumentTypes(docTypesData);

        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleMappingChange = (index, selectedDocTypeNames) => {
        const selectedDocTypeIds = documentTypes
            .filter(dt => selectedDocTypeNames.includes(dt.name))
            .map(dt => dt.id);

        setMappings(prev => {
            const newMappings = [...prev];
            newMappings[index] = { ...newMappings[index], document_type_ids: selectedDocTypeIds };
            return newMappings;
        });
    };

    const handleAddMapping = () => {
        setMappings(prev => [
            { permit_type: '', document_type_ids: [], background_color: '', isNew: true },
            ...prev
        ]);
    };

    const handleNewPermitTypeChange = (index, value) => {
        setMappings(prev => {
            const newMappings = [...prev];
            newMappings[index] = { ...newMappings[index], permit_type: value };
            return newMappings;
        });
    };

    const handleColorChange = (index, value) => {
        setMappings(prev => {
            const newMappings = [...prev];
            newMappings[index] = { ...newMappings[index], background_color: value };
            return newMappings;
        });
    };

    const handleCreatePermitType = (term) => {
        // Just return the term, the Select2Dropdown will pass it to onChange
        return term;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = mappings.map(({ permit_type, document_type_ids, background_color }) => ({
                permit_type,
                document_type_ids,
                background_color: background_color || ''
            }));

            const res = await fetch(`${ADMIN_API_URL}permit-document-mappings/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(JSON.stringify(errorData) || 'Failed to save mappings.');
            }

            toast.success('Permit to Document mappings saved successfully!');
            fetchData(); // Refresh data after save
            if (onClose) onClose(); // Close the dialog on successful save
        } catch (error) {
            toast.error(`Save failed: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>Loading configuration...</div>;
    }

    return (
        <div className="permit-config-container">
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '20px' }}>
                Map required document types to each permit. This will be used to filter and suggest documents when adding permit details.
            </p>
            
            {hasPermission('PERMITS', 'can_create') && (
                <div style={{ marginBottom: '1rem' }}>
                    <button className="add-btn" onClick={handleAddMapping}>
                        <i className="fa fa-plus"></i> Add New Permit Type
                    </button>
                </div>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table className="jobs-table">
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>Permit Type</th>
                            <th>Associated Document Types</th>
                            <th style={{ width: '15%' }}>Background Color</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.map((mapping, index) => {
                            const selectedDocTypeNames = documentTypes
                                .filter(dt => mapping.document_type_ids.includes(dt.id))
                                .map(dt => dt.name);

                            return (
                                <tr key={mapping.permit_type || `new-${index}`}>
                                    <td>
                                        {mapping.isNew ? (
                                            <Select2Dropdown
                                                options={mappings.filter(m => !m.isNew && m.permit_type).map(m => m.permit_type)}
                                                value={mapping.permit_type}
                                                onChange={(val) => handleNewPermitTypeChange(index, val)}
                                                placeholder="Select or create permit type"
                                                allowCreate={true}
                                                onCreateTag={handleCreatePermitType}
                                            />
                                        ) : (
                                            <span style={{ fontWeight: 'bold' }}>{mapping.permit_type}</span>
                                        )}
                                    </td>
                                    <td>
                                        <MultiSelectDropdown
                                            id={`permit-${index}`}
                                            options={documentTypes.map(dt => dt.name)}
                                            selectedValues={selectedDocTypeNames}
                                            onChange={(selectedNames) => handleMappingChange(index, selectedNames)}
                                            placeholder="Select document types..."
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="color"
                                            value={mapping.background_color || '#ffffff'}
                                            onChange={(e) => handleColorChange(index, e.target.value)}
                                            style={{ cursor: 'pointer', padding: '0', border: 'none', width: '40px', height: '40px', borderRadius: '4px' }}
                                            title="Select background color"
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Standard Dialog Footer */}
            <div className="dialog-footer" style={{ marginTop: '20px' }}>
                <button type="button" className="dialog-btn dialog-btn-secondary" onClick={onClose} disabled={saving}>
                    Cancel
                </button>
                {/* 3. Protect Save button */}
                {hasPermission('PERMITS', 'can_edit') && (
                    <button className="dialog-btn dialog-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Mappings'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default PermitConfiguration;